(() => {
  const clean = value => String(value ?? '').replace(/\s+/g, ' ').trim();
  const key = value => clean(value).toLocaleLowerCase('de-DE');
  const empty = value => ['', '-', '–', '—', '/'].includes(clean(value));
  const skuHeader = value => /^(?:aktuelle )?(artikel(?:nummer|[-\s]?(?:nr\.?|code))?|art\.?[-\s]?nr\.?|sku|product code|code)$/i.test(clean(value));
  const standardHeader = value => /^(?:(?:herstellerangaben(?: zu)?\s+)?(?:norm(?:en)?|standards?|kennzeichnung(?:en)?)(?:\b|\/)|herstellerangaben$)/i.test(clean(value));
  const technicalHeading = value => /technische (?:daten|angaben)/i.test(value);
  const create = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const valueCell = value => create('td', '', value);
  const same = (a, b) => clean(a.innerHTML) === clean(b.innerHTML);
  class ProductSpecifications extends HTMLElement {
    connectedCallback() {
      if (!this.ready && !this.querySelector('[data-ms-spec-config]')) {
        this.pending = setTimeout(() => { if (this.isConnected) this.connectedCallback(); }, 0);
        return;
      }
      this.controller?.abort();
      this.controller = new AbortController();
      if (!this.ready) this.initialize();
      document.addEventListener('variant:change', event => {
        const formId = this.getAttribute('form-id');
        if (event.target === document.getElementById(formId) || event.detail?.formId === formId) this.sync(event.detail.variant);
      }, { signal: this.controller.signal });
      const formId = this.getAttribute('form-id');
      document.getElementById(formId)?.addEventListener('product:rerender', event => {
        const updated = event.detail?.htmlFragment?.querySelector(`ms-product-specifications[form-id="${CSS.escape(formId)}"] [data-ms-spec-config]`);
        let selection = null;
        try { selection = JSON.parse(updated?.textContent || '{}').restockSelection; } catch {}
        this.syncRestock(selection);
      }, { signal: this.controller.signal });
      this.observer?.observe(this);
    }
    disconnectedCallback() {
      clearTimeout(this.pending);
      this.controller?.abort();
      this.observer?.disconnect();
    }
    initialize() {
      const configElement = this.querySelector('[data-ms-spec-config]');
      if (!configElement) return;
      try { this.config = JSON.parse(configElement.textContent); } catch { return; }
      this.source = this.querySelector('.ms-spec-source');
      if (!this.source) return;
      this.ready = true;
      this.labels = this.config.labels;
      this.variants = this.config.variants || [];
      this.tables = [];
      this.restockBlocks = this.config.restockSelection?.enabled ? [...this.source.querySelectorAll('[data-ms-restock]')].map(block => {
        const entries = [...block.querySelectorAll('p')].map(node => {
          const text = clean(node.querySelector('strong')?.textContent);
          const sku = text.match(/^[A-Z]{2}\d+[A-Z0-9]*/i)?.[0];
          return { node, sku };
        }).filter(entry => entry.sku);
        return { block, entries, singleVariant: this.variants.length === 1 && block.classList.contains('kratos-restock-info') };
      }) : [];
      const models = [...this.source.querySelectorAll('table, ul, ol')].map(node => node.tagName === 'TABLE' ? this.readTable(node) : this.readList(node)).filter(Boolean);
      for (let index = 0; index < models.length - 1; index++) {
        const common = models[index];
        const variants = models[index + 1];
        if (common.merged || common.rows.length !== 1 || common.headers.some(skuHeader) || !variants.headers.some(skuHeader) || common.node.parentElement !== variants.node.parentElement) continue;
        const between = [];
        let sibling = common.node.nextElementSibling;
        while (sibling && sibling !== variants.node && /^H[2-6]$/.test(sibling.tagName) && /varianten|ausführungen/i.test(sibling.textContent)) { between.push(sibling); sibling = sibling.nextElementSibling; }
        if (sibling !== variants.node) continue;
        const collisions = common.headers.map((header, i) => ({ i, target: variants.headers.findIndex(h => key(h) === key(header)) })).filter(pair => pair.target >= 0);
        if (collisions.some(pair => !variants.rows.every(row => same(row[pair.target], common.rows[0][pair.i])))) continue;
        common.headers.forEach((header, i) => {
          if (variants.headers.some(h => key(h) === key(header))) return;
          variants.headers.push(header);
          variants.rows.forEach(row => row.push(common.rows[0][i]));
        });
        common.node.replaceWith(variants.node);
        between.forEach(node => node.remove());
        common.merged = true;
      }
      const active = models.filter(model => !model.merged);
      active.forEach((model, index) => {
        if (active.length === 1 && !model.headers.some(skuHeader) && model.rows.length === 1) this.expandVariants(model);
        this.render(model, index);
      });
      this.source.querySelectorAll('h2,h3,h4').forEach(heading => {
        if (technicalHeading(heading.textContent)) heading.textContent = this.labels.data;
      });
      this.sync(this.config.selectedVariant);
      this.syncRestock(this.config.restockSelection);
      if (window.ResizeObserver) {
        this.observer = new ResizeObserver(() => this.updateOverflow());
        this.observer.observe(this);
      }
      this.updateOverflow();
    }
    readTable(node) {
      const rows = [...node.rows];
      if (!rows.length || rows.some(row => [...row.cells].some(cell => cell.colSpan > 1 || cell.rowSpan > 1))) return null;
      if (rows.every(row => row.cells.length === 2 && row.cells[0].tagName === 'TH' && row.cells[1].tagName === 'TD')) {
        return { node, headers: rows.map(row => clean(row.cells[0].textContent)), rows: [rows.map(row => row.cells[1])] };
      }
      const first = [...rows[0].cells];
      if (!first.every(cell => cell.tagName === 'TH') || rows.length < 2 || !rows.every(row => row.cells.length === first.length)) return null;
      return { node, headers: first.map(cell => clean(cell.textContent)), rows: rows.slice(1).map(row => [...row.cells]) };
    }
    readList(node) {
      if (node.closest('li') || node.querySelector('a,img,table,ul,ol')) return null;
      const items = [...node.children];
      const pairs = [];
      const leftovers = [];
      items.forEach(item => {
        const text = clean(item.textContent);
        const parts = text.split(/;\s*(?=[A-Za-zÄÖÜäöüß][^;:]{0,65}:)/);
        if (!parts.every(part => /^.{1,100}:\s*\S/.test(part))) { leftovers.push(item); return; }
        parts.forEach(part => {
          const colon = part.indexOf(':');
          pairs.push([part.slice(0, colon).trim(), valueCell(part.slice(colon + 1).trim())]);
        });
      });
      if (pairs.length < 2 || leftovers.length > items.length / 2) return null;
      const known = pairs.some(([header]) => /^(?:material|gewichte?|festigkeit|bruchlast|bandbreite|durchmesser|artikelnummer|aktuelle artikelnummer|norm|größe|grösstmaß|größtmaß|nutzlast|längen?)(?:\b|\s|\()/i.test(header));
      if (!known && !technicalHeading(node.previousElementSibling?.textContent || '')) return null;
      const table = create('table');
      node.before(table);
      items.filter(item => !leftovers.includes(item)).forEach(item => item.remove());
      if (!node.children.length) node.remove();
      if (!/^H[2-6]$/.test(table.previousElementSibling?.tagName || '') || !technicalHeading(table.previousElementSibling.textContent)) table.before(create('h3', '', this.labels.data));
      return { node: table, headers: pairs.map(pair => pair[0]), rows: [pairs.map(pair => pair[1])], fromList: true };
    }
    expandVariants(model) {
      if (!this.variants.length || !this.variants.every(variant => variant.sku)) return;
      const originalHeaders = [...model.headers];
      const facts = model.rows[0];
      const optionNames = this.config.optionNames || [];
      const optionIndices = optionNames.map((name, index) => ({ name, index })).filter(option => !/^title$/i.test(option.name) && !originalHeaders.some(header => key(header) === key(option.name)));
      const exactVariant = originalHeaders.map((header, index) => ({ header, index })).filter(pair => this.variants.some(variant => key(variant.title) === key(pair.header) || (variant.options || []).some(option => key(option) === key(pair.header))));
      const varied = new Set(exactVariant.map(pair => pair.index));
      const columns = originalHeaders.map((_, index) => index).filter(index => !varied.has(index));
      const hasVariantFacts = exactVariant.length && this.variants.every(variant => exactVariant.filter(pair => key(variant.title) === key(pair.header) || (variant.options || []).some(option => key(option) === key(pair.header))).length === 1);
      if (exactVariant.length && !hasVariantFacts) return;
      model.headers = [this.labels.articleNumber, ...optionIndices.map(option => option.name), ...columns.map(index => originalHeaders[index]), ...(hasVariantFacts ? [this.labels.data] : [])];
      model.rows = this.variants.map(variant => {
        const row = [valueCell(variant.sku), ...optionIndices.map(option => valueCell(variant.options[option.index])), ...columns.map(index => facts[index])];
        if (hasVariantFacts) {
          const match = exactVariant.find(pair => key(variant.title) === key(pair.header) || (variant.options || []).some(option => key(option) === key(pair.header)));
          row.push(facts[match.index]);
        }
        return row;
      });
    }
    cell(source, header) {
      const node = create('td');
      node.innerHTML = source.innerHTML;
      node.querySelectorAll('[id]').forEach(child => child.removeAttribute('id'));
      const value = clean(node.textContent);
      if (skuHeader(header) || /^(ean|barcode)$/i.test(header)) node.classList.add('ms-spec-code', 'ms-spec-atomic');
      else if (/^[\d\s.,/+–—−×x%°øØ±()-]+(?:\s*(?:g|kg|kN|N|mm|cm|m|ml|l|m\/s|g\/m))?$/.test(value) || (!value.includes(' ') && value.length <= 12)) node.classList.add('ms-spec-atomic');
      if (/^\d/.test(value) && !skuHeader(header)) node.classList.add('ms-spec-number');
      return node;
    }
    render(model, index) {
      const group = create('div', 'ms-spec-group');
      group.dataset.tableIndex = String(index);
      const shared = model.headers.map((header, i) => standardHeader(header) && model.rows.every(row => same(row[i], model.rows[0][i])) ? i : -1).filter(i => i >= 0);
      const standards = shared.filter(i => !empty(model.rows[0][i].textContent));
      if (standards.length) {
        const facts = create('dl', 'ms-spec-common');
        standards.forEach(i => {
          const fact = create('div');
          const value = create('dd');
          value.innerHTML = model.rows[0][i].innerHTML;
          fact.append(create('dt', '', this.labels.standards), value);
          facts.append(fact);
        });
        group.append(facts);
      }
      const columns = model.headers.map((_, i) => i).filter(i => !shared.includes(i));
      if (!columns.length) {
        model.node.replaceWith(group);
        return;
      }
      const table = create('table', 'ms-spec-table ms-spec-uniform');
      table.setAttribute('aria-label', this.labels.comparison);
      const head = create('thead');
      const headRow = create('tr');
      columns.forEach(i => {
        const header = create('th', '', skuHeader(model.headers[i]) ? this.labels.articleNumber : standardHeader(model.headers[i]) ? this.labels.standards : model.headers[i]);
        header.scope = 'col';
        headRow.append(header);
      });
      head.append(headRow);
      const body = create('tbody');
      const skuIndex = model.headers.findIndex(skuHeader);
      model.records = model.rows.map((cells, position) => {
        const row = create('tr');
        row.dataset.record = String(position);
        columns.forEach(i => row.append(this.cell(cells[i], model.headers[i])));
        body.append(row);
        return { row, sku: skuIndex < 0 ? '' : clean(cells[skuIndex].textContent), cells };
      });
      table.append(head, body);
      const note = create('p', 'ms-spec-scroll-note', this.labels.scroll);
      note.hidden = true;
      const scroll = create('div', 'ms-spec-scroll');
      scroll.setAttribute('role', 'region');
      scroll.setAttribute('aria-label', this.labels.comparison);
      scroll.tabIndex = -1;
      scroll.append(table);
      group.append(note, scroll);
      let replace = model.node;
      while (replace.parentElement !== this.source && replace.parentElement?.children.length === 1 && (replace.parentElement.classList.contains('table-wrapper') || /overflow/.test(replace.parentElement.getAttribute('style') || ''))) replace = replace.parentElement;
      replace.replaceWith(group);
      Object.assign(model, { group, scroll, note, table });
      this.tables.push(model);
    }
    sync(variant) {
      if (!this.ready) return;
      this.selected = variant;
      this.syncRestock(null);
      this.tables.forEach(model => model.records.forEach(record => {
        const selected = Boolean(variant?.sku && key(record.sku) === key(variant.sku));
        record.row.classList.toggle('ms-spec-active', selected);
        if (selected) record.row.setAttribute('aria-current', 'true');
        else record.row.removeAttribute('aria-current');
      }));
      this.updateOverflow();
    }
    syncRestock(selection) {
      const canShow = Boolean(selection?.enabled && selection.tracked && selection.variantId === this.selected?.id && selection.inventoryQuantity === 0);
      this.restockBlocks?.forEach(({ block, entries, singleVariant }) => {
        let matchingEntry = false;
        entries.forEach(({ node, sku }) => {
          const visible = canShow && key(sku) === key(selection.sku);
          node.hidden = !visible;
          matchingEntry ||= visible;
        });
        block.hidden = !(canShow && (matchingEntry || (entries.length === 0 && singleVariant)));
      });
    }
    updateOverflow() {
      this.tables.forEach(model => {
        const overflow = model.scroll.scrollWidth > model.scroll.clientWidth + 2;
        model.note.hidden = !overflow;
        model.scroll.tabIndex = overflow ? 0 : -1;
      });
    }
  }
  const start = () => {
    if (!customElements.get('ms-product-specifications')) customElements.define('ms-product-specifications', ProductSpecifications);
  };
  if (document.readyState === 'complete') start();
  else document.addEventListener('DOMContentLoaded', start, { once: true });
})();
